import { TypedDocumentNode, gql } from '@apollo/client';
import { isPast, isValid } from 'date-fns';

import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { NextTradeCountdown } from '@sorare/core/src/components/cardswap/NextTradeCountdown';
import { PickCardToSwap as PickCardToSwapDumb } from '@sorare/core/src/components/cardswap/steps/PickCardToSwap';
import { StepProps } from '@sorare/core/src/components/cardswap/types';
import { useCardSwapContext } from '@sorare/core/src/components/cardswap/useCardSwapContext';
import { isType } from '@sorare/core/src/gql';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { sortBy } from '@sorare/core/src/lib/arrays';

import { InfoModal } from 'components/cards/InfoModal';
import { CardSelection } from 'components/cardswap/CardSelection';
import { CardSelection_NBACard } from 'components/cardswap/CardSelection/__generated__/index.graphql';
import { CardToReceive } from 'components/cardswap/CardToReceive';

import {
  NBACardSwapFetchQuery,
  NBACardSwapFetchQueryVariables,
  NBAStartCardTradeMutation,
  NBAStartCardTradeMutationVariables,
} from './__generated__/index.graphql';

const NBA_START_CARD_TRADE_MUTATION = gql`
  mutation NBAStartCardTradeMutation($slug: String!) {
    startNBACardTrade(userGiveCardSlug: $slug) {
      id
      userReceiveCardChoices {
        id
        ...CardToReceive_NBACommonCardSample
      }
      isComplete
    }
  }
  ${/* eslint-disable-next-line sorare/no-detached-fragments */ ''}
  ${CardToReceive.fragments.NBACommonCardSample}
` as TypedDocumentNode<
  NBAStartCardTradeMutation,
  NBAStartCardTradeMutationVariables
>;

const NBA_CARD_SWAP_FETCH_QUERY = gql`
  query NBACardSwapFetchQuery {
    currentUser {
      slug
      # eslint-disable-next-line @graphql-eslint/no-deprecated
      nextNBACardTradeAvailableAfter
      # eslint-disable-next-line @graphql-eslint/no-deprecated
      latestNBACardTrade {
        id
        expiresAfter
        isComplete
        userGiveCard {
          id
          slug
        }
      }
      cards(first: 150, rarities: [common], sport: NBA) {
        nodes {
          slug
          ...CardSelection_NBACard
        }
      }
    }
  }
  ${CardSelection.fragments.NBACard}
` as TypedDocumentNode<NBACardSwapFetchQuery, NBACardSwapFetchQueryVariables>;

type Props = StepProps;

export const PickCardToSwap = ({ nextStep }: Props) => {
  const [startCardTrade] = useMutation(NBA_START_CARD_TRADE_MUTATION, {
    warnIfNoErrorHandling: false,
  });

  const { setCardToSwap, setTradeId } = useCardSwapContext();

  const { data } = useQuery(NBA_CARD_SWAP_FETCH_QUERY, {
    fetchPolicy: 'network-only',
  });

  if (!data) {
    return <LoadingIndicator />;
  }

  const trade = data.currentUser?.latestNBACardTrade;
  const tradeInProgress = trade && !trade.isComplete;

  const nextTradeDate = data?.currentUser?.nextNBACardTradeAvailableAfter;
  if (!isValid(nextTradeDate)) {
    return null;
  }
  const isTradeAvailable =
    tradeInProgress || (nextTradeDate && isPast(nextTradeDate));
  if (!isTradeAvailable && nextTradeDate) {
    return <NextTradeCountdown next={nextTradeDate} />;
  }

  if (tradeInProgress && trade.userGiveCard?.slug) {
    setTradeId(trade.id);
    setCardToSwap(trade.userGiveCard.slug);
    nextStep();
    return null;
  }

  const unsorted =
    data?.currentUser?.cards?.nodes.filter(c => isType(c, 'NBACard')) || [];
  const sorted = sortBy(
    card =>
      (isType(card.anyPlayer, 'NBAPlayer') &&
        card.anyPlayer.tenGameAverageGameStats.score) ||
      0,
    [...unsorted]
  ).reverse();

  const submit = async (selectedSlug: string) => {
    const { data: response } = await startCardTrade({
      variables: {
        slug: selectedSlug,
      },
    });
    const tradeId = response?.startNBACardTrade.id;
    if (tradeId) setTradeId(tradeId);
    setCardToSwap(selectedSlug);
  };

  const cardSlugs: string[] = sorted.map(({ slug }) => slug);
  const cardDictionary = sorted.reduce<Record<string, CardSelection_NBACard>>(
    (map, card) => {
      map[card.slug] = card;
      return map;
    },
    {}
  );

  return (
    <PickCardToSwapDumb
      cardOptions={cardSlugs}
      renderCard={(
        cardSlug,
        { isDismissed, isSelected, onSelect, onDisplayPlayerInfo }
      ) => {
        return (
          <CardSelection
            key={cardSlug}
            card={cardDictionary[cardSlug]}
            isDismissed={isDismissed}
            isSelected={isSelected}
            onSelect={onSelect}
            onDisplayPlayerInfo={onDisplayPlayerInfo}
          />
        );
      }}
      submit={submit}
      nextStep={nextStep}
      drawerContent={cardSlug => <InfoModal cardSlug={cardSlug} />}
    />
  );
};
